import { z } from "zod";
import { ActorRefSchema, TimestampSchema } from "./common.js";
import { IdSchema, NamespacedNameSchema } from "./ids.js";
/**
 * Side-effect class of a tool. Only `external` and `spend` need approval by default;
 * `intercompany` needs it only when the company's `intercompany.approval` setting says so.
 */
export const SIDE_EFFECTS = ["none", "internal", "intercompany", "external", "spend"];
export const SideEffectSchema = z.enum(SIDE_EFFECTS);
export const CostHintSchema = z.object({
    tier: z.enum(["local", "api", "browser"]),
    estUsd: z.number().nonnegative().optional(),
});
/** Static, serializable description of a tool (no handler, schemas as JSON Schema). */
export const ToolDescriptorSchema = z.object({
    name: NamespacedNameSchema,
    description: z.string().min(1),
    sideEffect: SideEffectSchema,
    requiredPermissions: z.array(NamespacedNameSchema),
    costHint: CostHintSchema.optional(),
    inputSchema: z.record(z.string(), z.unknown()),
    outputSchema: z.record(z.string(), z.unknown()),
});
/** Builds the serializable descriptor of a tool, exporting its Zod schemas as JSON Schema. */
export function describeTool(tool) {
    return {
        name: tool.name,
        description: tool.description,
        sideEffect: tool.sideEffect,
        requiredPermissions: tool.requiredPermissions,
        ...(tool.costHint ? { costHint: tool.costHint } : {}),
        inputSchema: z.toJSONSchema(tool.input, { unrepresentable: "any" }),
        outputSchema: z.toJSONSchema(tool.output, { unrepresentable: "any" }),
    };
}
/** A tool call as requested by an agent or a UI action. */
export const ToolCallSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    input: z.unknown(),
});
export const ApprovalStatusSchema = z.enum(["pending", "approved", "rejected", "expired"]);
/** Created when a gated tool call needs a human decision (DESIGN §5). */
export const ApprovalRequestSchema = z.object({
    id: IdSchema,
    companyId: IdSchema,
    toolName: NamespacedNameSchema,
    input: z.unknown(),
    sideEffect: SideEffectSchema,
    requestedBy: ActorRefSchema,
    onBehalfOf: IdSchema.nullable(),
    /** The user who must decide (acting user, or company owner above the spend threshold). */
    approverUserId: IdSchema,
    taskId: IdSchema.nullable(),
    status: ApprovalStatusSchema,
    createdAt: TimestampSchema,
    decidedAt: TimestampSchema.nullable(),
    decidedBy: IdSchema.nullable(),
    note: z.string().nullable(),
});
/** "Always allow" rule. `inputMatch` is a subset that the call input must deep-equal. */
export const StandingApprovalSchema = z.object({
    id: IdSchema,
    companyId: IdSchema,
    userId: IdSchema,
    toolName: NamespacedNameSchema,
    inputMatch: z.record(z.string(), z.unknown()),
    createdAt: TimestampSchema,
    revokedAt: TimestampSchema.nullable(),
});
//# sourceMappingURL=tool.js.map